import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { auth } from '../firebase/config'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  const INACTIVITY_TIMEOUT = 12 * 60 * 60 * 1000 // 12 hours in milliseconds

  // Update last activity time
  const updateLastActivity = () => {
    localStorage.setItem('lastActivity', Date.now().toString())
  }

  const logout = useCallback(async () => {
    try {
      localStorage.removeItem('lastActivity')
      await signOut(auth)
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }, [])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user)
      setLoading(false)
      if (user) {
        // Reset inactivity timer on auth state change
        updateLastActivity()
      }
    })

    return unsubscribe
  }, [])

  // Check for inactivity and logout
  useEffect(() => {
    if (!user) return

    // Update activity on user interactions
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart']
    const handleActivity = () => {
      updateLastActivity()
    }

    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity)
    })

    // Check inactivity periodically
    const inactivityCheck = setInterval(() => {
      const lastActivity = localStorage.getItem('lastActivity')
      if (lastActivity) {
        const timeSinceLastActivity = Date.now() - parseInt(lastActivity)
        if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
          // Auto logout after 12 hours of inactivity
          logout()
          clearInterval(inactivityCheck)
        }
      }
    }, 60000) // Check every minute

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity)
      })
      clearInterval(inactivityCheck)
    }
  }, [user, logout])

  const value = {
    user,
    loading,
    logout
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

